import { useState, ChangeEvent, useCallback } from "react";
import { format } from "date-fns";
import { usePropertyValidation } from "./usePropertyValidation";

// Type for preloaded images that store the actual file data to prevent stale references
export interface PreloadedImage {
  name: string;
  type: string;
  size: number;
  data: ArrayBuffer;
  previewUrl: string;
}

export const usePropertyForm = () => {
  // Property location fields
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  
  // Geocoded coordinates
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  
  // Property details fields
  const [bedrooms, setBedrooms] = useState("");
  const [bathrooms, setBathrooms] = useState("");
  const [squareFeet, setSquareFeet] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [units, setUnits] = useState("");

  const [foundationType, setFoundationType] = useState("");
  const [yearBuilt, setYearBuilt] = useState("");
  const [lotSize, setLotSize] = useState("");
  const [roofAge, setRoofAge] = useState("");
  const [windowType, setWindowType] = useState("");
  const [furnaceAge, setFurnaceAge] = useState("");
  const [waterHeaterAge, setWaterHeaterAge] = useState("");
  const [architecture, setArchitecture] = useState("");
  const [parking, setParking] = useState("");
  
  // Financial details fields
  const [price, setPrice] = useState("");
  const [arv, setArv] = useState("");
  const [zillowLink, setZillowLink] = useState("");
  const [contractDate, setContractDate] = useState<Date | undefined>(undefined);
  
  // Description field
  const [description, setDescription] = useState("");
  
  // Image handling - now stores preloaded data instead of raw File objects
  const [selectedImages, setSelectedImages] = useState<PreloadedImage[]>([]);
  
  // Handle coordinates change from GoogleMapPreview
  const handleCoordinatesChange = useCallback((lat: number, lng: number) => {
    setLatitude(lat);
    setLongitude(lng);
  }, []);
  
  // Handle image selection - immediately reads file data to prevent stale references
  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const newFiles = Array.from(e.target.files);
    
    // Immediately read file data into memory to prevent stale file references
    // This is critical for mobile browsers that may release file handles
    const preloadedImages = await Promise.all(
      newFiles.map(async (file): Promise<PreloadedImage> => {
        const arrayBuffer = await file.arrayBuffer();
        const previewUrl = URL.createObjectURL(file);
        return {
          name: file.name,
          type: file.type,
          size: file.size,
          data: arrayBuffer,
          previewUrl
        };
      })
    );
    
    setSelectedImages(prev => [...prev, ...preloadedImages]);
  };
  
  // Remove an image from the selected list
  const removeImage = (index: number) => {
    // Clean up the object URL to prevent memory leaks
    URL.revokeObjectURL(selectedImages[index]?.previewUrl);
    
    // Remove the image from state
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Reorder images for drag-and-drop
  const reorderImages = (fromIndex: number, toIndex: number) => {
    setSelectedImages(prev => {
      const newImages = [...prev];
      const [movedImage] = newImages.splice(fromIndex, 1);
      newImages.splice(toIndex, 0, movedImage);
      return newImages;
    });
  };

  // Convert preloaded images back to File objects for upload
  // This creates fresh File objects from the stored ArrayBuffer data
  const getFilesForUpload = (): File[] => {
    return selectedImages.map(img => 
      new File([img.data], img.name, { type: img.type })
    );
  };

  // Get preview URLs for display (derived from selectedImages)
  const imagePreviewUrls = selectedImages.map(img => img.previewUrl);

  const { validatePropertyData } = usePropertyValidation();

  // Inline field-level errors (keyed by validation field name)
  const [errors, setErrors] = useState<Record<string, string>>({});
  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);
  
  // Create the property data object for submission
  const createPropertyData = (userId: string) => {
    // Format the price and ARV values (remove $ and commas)
    const formattedPrice = price.replace(/[$,]/g, '');
    const formattedArv = arv.replace(/[$,]/g, '');
    
    return {
      seller_id: userId,
      address,
      city,
      state,
      zip_code: zipCode,
      bedrooms: bedrooms ? parseInt(bedrooms) : 0,
      bathrooms: bathrooms ? parseFloat(bathrooms) : 0,
      square_feet: squareFeet ? parseInt(squareFeet) : 0,
      property_type: propertyType,
      units: propertyType === 'multi-unit' && units ? parseInt(units) : null,

      foundation_type: foundationType || null,
      year_built: yearBuilt ? parseInt(yearBuilt) : null,
      lot_size: lotSize ? lotSize.replace(/,/g, '') : null,
      roof_age: roofAge || null,
      window_type: windowType || null,
      furnace_age: furnaceAge || null,
      water_heater_age: waterHeaterAge || null,
      architecture_style: architecture || null,
      parking_type: parking || null,
      price: formattedPrice ? parseFloat(formattedPrice) : 0,
      arv_value: formattedArv ? parseFloat(formattedArv) : null,
      zillow_link: zillowLink || null,
      contract_expiration_date: contractDate ? format(contractDate, "yyyy-MM-dd") : null,
      status: 'active',
      description: description || null,
      image_url: undefined as string | undefined,
      // Include coordinates if available
      latitude: latitude,
      longitude: longitude
    };
  };
  
  const validateForm = () => {
    const formData = {
      address, city, state, zipCode, bedrooms, bathrooms, price, foundationType, arv,
      propertyType, contractDate, units
    };


    // Pass File objects for validation (reconstructed from preloaded data)
    const result = validatePropertyData(formData, getFilesForUpload());
    setErrors(result ?? {});
    return result;
  };

  return {
    // Location props
    address, setAddress,
    city, setCity,
    state, setState,
    zipCode, setZipCode,
    
    // Coordinates
    latitude, longitude,
    handleCoordinatesChange,
    
    // Details props
    bedrooms, setBedrooms,
    bathrooms, setBathrooms,
    squareFeet, setSquareFeet,
    propertyType, setPropertyType,
    units, setUnits,

    foundationType, setFoundationType,
    yearBuilt, setYearBuilt,
    lotSize, setLotSize,
    roofAge, setRoofAge,
    windowType, setWindowType,
    furnaceAge, setFurnaceAge,
    waterHeaterAge, setWaterHeaterAge,
    architecture, setArchitecture,
    parking, setParking,
    
    // Financial props
    price, setPrice,
    arv, setArv,
    zillowLink, setZillowLink,
    contractDate, setContractDate,
    
    // Description & images props
    description, setDescription,
    selectedImages,
    imagePreviewUrls,
    handleImageChange,
    removeImage,
    reorderImages,
    getFilesForUpload,
    
    // Data creation
    createPropertyData,
    validateForm,

    // Inline errors
    errors,
    setErrors,
    clearFieldError,
  };
};
